import { MenuItem } from '@mui/material';
import { Grid } from '@mui/system';
import { useDialog } from 'app/context';
import { useReportList } from 'app/hooks';
import {
  Breadcrumb,
  Button,
  InputSearchField,
  List,
  ListWrapperDiv,
  SearchCompany,
  SearchDoctor,
  SearchPatient,
  TextField,
} from 'app/shared';
import { formatDate, ROUTES } from 'app/utils';
import { ICompanySchema, IDoctorSchema, IPatientSchema, IReportStatus } from 'generated/axios';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const ReportList = () => {
  const {
    list,
    page,
    handlePageChange,
    pageSize,
    handlePageSizeChange,
    count,
    loading,
    getPdf,
    additionalFiltersForm,
    deleteByIds,
  } = useReportList();

  const navigate = useNavigate();

  const { createDialog, closeDialog, createSimpleConfirm } = useDialog();

  const { bindTextField, bindSelect, setValues, submit } = additionalFiltersForm;

  const [selectedPatient, setSelectedPatient] = useState<IPatientSchema>();

  const [selectedDoctor, setSelectedDoctor] = useState<IDoctorSchema>();

  const [selectedCompany, setSelectedCompany] = useState<ICompanySchema>();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleselectedIdsChange = (ids: string[]) => {
    setSelectedIds(ids);
  };

  useEffect(() => {
    setValues([
      { field: 'patientId', value: selectedPatient?.id },
      { field: 'doctorId', value: selectedDoctor?.id },
      { field: 'companyId', value: selectedCompany?.id },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient, selectedDoctor, selectedCompany]);

  const getReportStatus = (status: IReportStatus | undefined) => {
    switch (status) {
      case 'draft':
        return t('report.status.draft');
      case 'processed':
        return t('report.status.processed');
      case 'confirmed':
        return t('report.status.confirmed');
      case 'error':
        return t('report.status.error');
      default: //in case of new non-translated values
        return status ?? '';
    }
  };

  //search patient dialog
  const handlePatientDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchPatient
          onCancel={closeDialog}
          onConfirmPatient={(patient) => {
            setSelectedPatient(patient);
            closeDialog();
          }}
        />
      ),
    });

  const handleCancelPatient = () => setSelectedPatient(undefined);

  //search doctor dialog
  const handleDoctorDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchDoctor
          onCancel={closeDialog}
          onConfirmDoctor={(doctor) => {
            setSelectedDoctor(doctor);
            closeDialog();
          }}
        />
      ),
    });

  const handleCancelDoctor = () => setSelectedDoctor(undefined);

  //search company dialog
  const handleCompanyDialog = () =>
    createDialog({
      open: true,
      maxWidth: 'md',
      fullWidth: true,
      children: (
        <SearchCompany
          onCancel={closeDialog}
          onConfirmCompany={(company) => {
            setSelectedCompany(company);
            closeDialog();
          }}
        />
      ),
    });

  const handleCancelCompany = () => setSelectedCompany(undefined);

  const confirmDeleteReports = () => {
    createSimpleConfirm({
      color: 'error.main',
      title: t('actions.delete'),
      message: t('reports.confirmDeleteBulkMessage'),
      onConfirm: () => deleteByIds(selectedIds),
    });
  };

  return (
    <>
      <Breadcrumb />
      <ListWrapperDiv>
        <Grid container spacing={2} paddingBottom={2} display={'flex'} alignItems={'center'}>
          <Grid size={4}>
            <InputSearchField
              size="small"
              label={t('common.doctor')}
              fieldValue={(selectedDoctor?.last_name ?? '') + ' ' + (selectedDoctor?.first_name ?? '')}
              onSearch={handleDoctorDialog}
              onCancel={handleCancelDoctor}
            />
          </Grid>
          <Grid size={4}>
            <InputSearchField
              size="small"
              label={t('patient.company')}
              fieldValue={selectedCompany?.name}
              onSearch={handleCompanyDialog}
              onCancel={handleCancelCompany}
            />
          </Grid>
          <Grid size={4}>
            <InputSearchField
              size="small"
              label={t('common.patient')}
              fieldValue={(selectedPatient?.last_name ?? '') + ' ' + (selectedPatient?.first_name ?? '')}
              onSearch={handlePatientDialog}
              onCancel={handleCancelPatient}
            />
          </Grid>
          <Grid size={3}>
            <TextField label={t('common.status')} size="small" {...bindSelect({ field: 'reportStatus' })}>
              <MenuItem key={'no_status'} value={undefined}>
                &nbsp;
              </MenuItem>
              {Object.entries(IReportStatus).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {getReportStatus(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={3}>
            <TextField
              label={t('actions.search')}
              size="small"
              {...bindTextField({ field: 'searchQuery' })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  submit();
                  e.preventDefault();
                }
              }}
            />
          </Grid>
          <Grid size={4}>
            <Button
              loading={loading}
              onClick={() => {
                submit();
              }}
            >
              {t('actions.search')}
            </Button>
          </Grid>
        </Grid>

        <List
          list={list}
          count={count}
          page={page}
          pageSize={pageSize}
          keyGetter={(el) => el.id}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          selectedIds={selectedIds}
          onSelectedIdsChange={handleselectedIdsChange}
          keepSelectedIds
          loading={loading}
          gridTemplateColumns="130px 130px 1fr 0.7fr 1fr 0.8fr 140px"
          listMaxHeight="calc(100vh - 370px)"
          onItemClick={(el) => navigate(el.id)}
          actions={
            <>
              <Button onClick={() => navigate(ROUTES.new)} loading={loading}>
                {t('actions.create')}
              </Button>
              <Button onClick={confirmDeleteReports} loading={loading} disabled={isEmpty(selectedIds)} color="error">
                {t('actions.deleteSelected')}
              </Button>
            </>
          }
          fields={[
            {
              title: t('common.createdAt'),
              key: 'created_at',
              valueGetter: (item) => formatDate(item.created_at),
            },
            {
              title: t('common.status'),
              key: 'report_status',
              valueGetter: (item) => getReportStatus(item.report_status),
            },
            {
              title: t('common.patient'),
              valueGetter: ({ patient }) => (patient?.last_name ?? '') + ' ' + (patient?.first_name ?? ''),
            },
            { title: t('patient.job'), key: 'patient', path: ['job_title'] },
            {
              title: t('common.doctor'),
              valueGetter: ({ doctor }) => (doctor?.last_name ?? '') + ' ' + (doctor?.first_name ?? ''),
            },
            {
              title: t('company.name'),
              key: 'patient',
              path: ['company', 'name'],
            },
            {
              title: <></>,

              cellRenderer: (item) => (
                <div>
                  {item.has_pdf && (
                    <Button
                      sx={{ margin: 0, fontSize: '12px' }}
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        getPdf(item.id);
                      }}
                    >
                      {t('actions.downloadPdf')}
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </ListWrapperDiv>
    </>
  );
};
